import { z } from 'zod'

const aiSpendGroup = z.object({
    key: z.string(),
    calls: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    searches: z.number(),
    costMicrodollars: z.number(),
})

const whatsappSpendGroup = z.object({
    key: z.string(),
    paidBy: z.string(),
    messages: z.number(),
    costMicros: z.number(),
})

// OpenAI amounts are in millionths of a dollar, WhatsApp amounts in millionths of whatsappCurrency.
export const spend = z.object({
    usdToEur: z.number(),
    whatsappCurrency: z.string(),
    openai: z.object({
        costMicrodollars: z.number(),
        storageMicrodollars: z.number(),
        calls: z.number(),
        searches: z.number(),
        unpricedCalls: z.number(),
        bySource: z.array(aiSpendGroup),
        byModel: z.array(aiSpendGroup),
    }),
    whatsapp: z.object({
        evaaMicros: z.number(),
        customerMicros: z.number(),
        delivered: z.number(),
        pending: z.number(),
        unpriced: z.number(),
        metaEvaaMicros: z.number(),
        bySource: z.array(whatsappSpendGroup),
        byCategory: z.array(whatsappSpendGroup),
    }),
    perVisitorMessage: z.object({
        visitorMessages: z.number(),
        costMicrodollars: z.number(),
    }),
    perConversation: z.object({
        conversations: z.number(),
        aiMicrodollars: z.number(),
        whatsappMicros: z.number(),
    }),
})

const comparison = z.object({
    current: z.number(),
    previous: z.number(),
})

// Each metric for the period next to the same metric for the period of the same length before it.
export const platformOverview = z.object({
    previousStart: z.string(),
    previousEnd: z.string(),
    metrics: z.object({
        companies: comparison,
        subscriptions: comparison,
        users: comparison,
        spendEurMicros: comparison,
        candidates: comparison,
        whatsappMessages: comparison,
        chats: comparison,
        visitorMessages: comparison,
    }),
    perDay: z.array(
        z.object({
            date: z.string(),
            companies: z.number(),
            candidates: z.number(),
            whatsappMessages: z.number(),
            chats: z.number(),
        })
    ),
})

const usage = z.object({
    used: z.number(),
    limit: z.number(),
})

export const customerStatus = z.enum(['paying', 'free', 'trial', 'past_due', 'none'])
export const customerFlag = z.enum(['loss', 'near_limit', 'inactive'])

// Cost is in millionths of a euro; monthlyAmount and revenue are in cents, estimated from the plan price after coupons.
export const customerOverview = z.object({
    nearLimit: z.number(),
    inactiveDays: z.number(),
    companies: z.array(
        z.object({
            id: z.string(),
            name: z.string().nullable(),
            tier: z.string().nullable(),
            period: z.string().nullable(),
            status: customerStatus,
            createdAt: z.coerce.date().nullable(),
            candidates: z.number(),
            chats: z.number(),
            conversations: usage.nullable(),
            messages: usage.nullable(),
            costEurMicros: z.number(),
            monthlyAmount: z.number().nullable(),
            revenue: z.number().nullable(),
            lastActiveAt: z.coerce.date().nullable(),
            flags: z.array(customerFlag),
        })
    ),
})

const usageAttention = {
    usage: z.number(),
    limit: z.number(),
    trial: z.boolean(),
}

/** What needs the customer's attention, each with what the dashboard links to. */
export const dashboardAttention = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('trial_ending'), endsAt: z.coerce.date() }),
    z.object({ kind: z.literal('candidates_almost_used'), ...usageAttention }),
    z.object({ kind: z.literal('candidates_used_up'), ...usageAttention }),
    z.object({ kind: z.literal('campaign_blocked'), campaignId: z.string(), campaign: z.string() }),
    z.object({ kind: z.literal('unanswered_questions'), siteId: z.string(), site: z.string(), count: z.number() }),
])

export const customerDashboard = z.object({
    // This billing period, or the whole free trial.
    period: z.object({
        candidates: z.number(),
        finished: z.number(),
        goodMatches: z.number(),
        chats: z.number().optional(),
    }),
    attention: dashboardAttention.array(),
    usesLeadForms: z.boolean(),
    canInvite: z.boolean().optional(),
    users: z.number().optional(),
    usage: z.number().optional(),
    limit: z.number().optional(),
    overageUsage: z.number().optional(),
    overageUnitAmount: z.number().optional(),
})

export const adminTab = z.enum(['overview', 'customers', 'spend'])

export const dashboardQuery = z.object({
    start: z.string().optional(),
    end: z.string().optional(),
    tab: adminTab.optional(),
})

export type DashboardQuery = z.infer<typeof dashboardQuery>
export type AdminTab = z.infer<typeof adminTab>
export type PeriodQuery = Pick<DashboardQuery, 'start' | 'end'>
export type Spend = z.infer<typeof spend>
export type PlatformOverview = z.infer<typeof platformOverview>
export type CustomerOverview = z.infer<typeof customerOverview>
export type CustomerRow = CustomerOverview['companies'][number]
export type CustomerFlag = z.infer<typeof customerFlag>
export type CustomerDashboard = z.infer<typeof customerDashboard>
export type DashboardAttention = z.infer<typeof dashboardAttention>
